/**
 * Client match store: optimistic updates + a persisted retry queue.
 *
 * Every mutation is applied locally with the pure game module first, then queued.
 * The queue is flushed in order; network/5xx failures are retried with backoff
 * (and on the `online` event), while 4xx responses mean the local copy diverged,
 * so the remaining ops for that match are dropped and the server copy is reloaded.
 */
import { useSyncExternalStore } from 'react';
import {
  applyDeal,
  matchStatus,
  rescore,
  totals,
  undoLastDeal,
  continueMatch as continueMatchState,
  finishMatch as finishMatchState,
  fitRulesToDeals,
  upgradeLegacyMatch,
  upgradeRules,
  type DealInput,
  type DealRecord,
  type StoredDeal,
  type HouseRules,
  type MatchState,
  type Seat,
} from '../game/match';
import type {
  CreateMatchBody,
  CreateMatchResponse,
  MatchResponse,
  MatchStatusColumn,
  SeatPlayer,
  Teams,
} from '../shared/schemas';
import { nameKey } from '../shared/constants';
import { ApiError, NetworkError, api } from './api';
import { KEYS, load, save } from './storage';
import { toast } from './toast';

type OpKind = 'spin' | 'deal' | 'undo' | 'patch' | 'finish' | 'continue';
interface Op {
  id: string;
  code: string;
  kind: OpKind;
  body: unknown;
  attempts: number;
}

export interface RecentMatch {
  code: string;
  teamA: string;
  teamB: string;
  guaaA: number;
  guaaB: number;
  status: MatchStatusColumn;
  dealsPlayed: number;
  updatedAt: string;
}

export interface SavedPlayer {
  playerId?: string;
  name: string;
  avatarKey: string | null;
  /** When they last played (ms); most recent first in pickers. */
  lastUsed?: number;
}

export interface SavedTeam {
  /** A name for this lineup, e.g. "Reyzak vs Thanshu" — editable, shown in the picker. */
  label: string;
  /** The four players in seat order (N, E, S, W): seats 0/2 are Team A, 1/3 are Team B. */
  players: SavedPlayer[];
  teams: Teams;
  lastUsed: number;
}

interface Snapshot {
  matches: Record<string, MatchResponse>;
  queue: Op[];
  online: boolean;
  syncing: boolean;
  /** Codes currently being fetched / that failed to load. */
  loading: Record<string, boolean>;
  errors: Record<string, string>;
}

/** Brings matches cached by older app versions (points, then trick counts) up to Saved/Guaa. */
function upgrade(data: MatchResponse): MatchResponse {
  const stored = data.deals as StoredDeal[];
  const up = upgradeLegacyMatch(data.match.rules, data.match.firstDealerSeat, stored);
  if (up) return { match: { ...data.match, rules: up.rules }, deals: up.deals };
  // Fill in rule fields added later (e.g. `extraRounds`); old sudden-death deals get extra rounds.
  const rules = fitRulesToDeals(upgradeRules(data.match.rules), stored.length);
  data = { ...data, match: { ...data.match, rules, closedAt: data.match.closedAt ?? (data.match.status === 'finished' ? data.match.finishedAt : null) } };
  if (stored.every((d) => d.result)) return data;
  // Trick-count era: a deal that gave Guaa was lost by the Rang team.
  const deals = stored.map((d): DealRecord => {
    const result = d.result ?? (d.guaaA + d.guaaB > 0 ? 'guaa' : 'saved');
    return { ...d, result, isCourt: d.isCourt && result === 'guaa' };
  });
  return { ...data, deals };
}

let snap: Snapshot = {
  matches: Object.fromEntries(Object.entries(load<Record<string, MatchResponse>>(KEYS.matches, {})).map(([k, v]) => [k, upgrade(v)])),
  queue: load(KEYS.queue, []),
  online: navigator.onLine,
  syncing: false,
  loading: {},
  errors: {},
};
const listeners = new Set<() => void>();

function set(patch: Partial<Snapshot>) {
  snap = { ...snap, ...patch };
  if (patch.matches) save(KEYS.matches, snap.matches);
  if (patch.queue) save(KEYS.queue, snap.queue);
  listeners.forEach((l) => l());
}

export function useStore<T>(selector: (s: Snapshot) => T): T {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => selector(snap),
  );
}

// ---------------------------------------------------------------------------
// Tokens, recents, saved players
// ---------------------------------------------------------------------------

let tokens = load<Record<string, string>>(KEYS.tokens, {});
export const getToken = (code: string): string | null => tokens[code] ?? null;
export function setToken(code: string, token: string) {
  tokens = { ...tokens, [code]: token };
  save(KEYS.tokens, tokens);
  listeners.forEach((l) => l());
}
export const useCanEdit = (code: string) => useStore(() => Boolean(tokens[code]));

let recent = load<RecentMatch[]>(KEYS.recent, []);
export const useRecent = () => useStore(() => recent);
function touchRecent(data: MatchResponse) {
  const t = totals(data.deals);
  const entry: RecentMatch = {
    code: data.match.code,
    teamA: data.match.teams.A.name,
    teamB: data.match.teams.B.name,
    guaaA: t.guaaA,
    guaaB: t.guaaB,
    status: data.match.status,
    dealsPlayed: data.deals.length,
    updatedAt: new Date().toISOString(),
  };
  recent = [entry, ...recent.filter((r) => r.code !== entry.code)].slice(0, 20);
  save(KEYS.recent, recent);
}
export function forgetRecent(code: string) {
  recent = recent.filter((r) => r.code !== code);
  save(KEYS.recent, recent);
  listeners.forEach((l) => l());
}

/** Saved players, most recently played first. */
export function getSavedPlayers(): SavedPlayer[] {
  return load<SavedPlayer[]>(KEYS.players, [])
    .slice()
    .sort((a, b) => (b.lastUsed ?? 0) - (a.lastUsed ?? 0));
}

/** The four players (in seat order) of the most recent match on this device. */
export const getLastPlayers = () => load<SavedPlayer[] | null>(KEYS.lastPlayers, null);

/** Remembers a match's players so the next match can pick them instead of retyping. */
export function rememberPlayers(seats: SeatPlayer[], asLastMatch: boolean) {
  const now = Date.now();
  const byName = new Map(getSavedPlayers().map((p) => [nameKey(p.name), p]));
  for (const s of seats) {
    const prev = byName.get(nameKey(s.name));
    byName.set(nameKey(s.name), {
      playerId: s.playerId ?? prev?.playerId,
      name: s.name,
      // Keep a known photo if this match was set up without one.
      avatarKey: s.avatarKey ?? prev?.avatarKey ?? null,
      lastUsed: asLastMatch ? now : (prev?.lastUsed ?? now - 1),
    });
  }
  const list = [...byName.values()].sort((a, b) => (b.lastUsed ?? 0) - (a.lastUsed ?? 0)).slice(0, 40);
  save(KEYS.players, list);
  if (asLastMatch) save(KEYS.lastPlayers, seats.map((s) => byName.get(nameKey(s.name))!));
  listeners.forEach((l) => l());
}

export function forgetSavedPlayer(name: string) {
  save(KEYS.players, getSavedPlayers().filter((p) => p.name !== name));
  listeners.forEach((l) => l());
}

/**
 * Renames a saved player and/or changes their photo. Keeps `lastUsed` so their
 * place in the "most recent" ordering doesn't change just from editing them.
 */
export function editSavedPlayer(oldName: string, patch: { name: string; avatarKey: string | null }) {
  const key = nameKey(oldName);
  const list = load<SavedPlayer[]>(KEYS.players, []);
  save(
    KEYS.players,
    list.map((p) => (nameKey(p.name) === key ? { ...p, name: patch.name, avatarKey: patch.avatarKey } : p)),
  );
  const lastList = load<SavedPlayer[] | null>(KEYS.lastPlayers, null);
  if (lastList) {
    save(
      KEYS.lastPlayers,
      lastList.map((p) => (nameKey(p.name) === key ? { ...p, name: patch.name, avatarKey: patch.avatarKey } : p)),
    );
  }
  listeners.forEach((l) => l());
}

// ---------------------------------------------------------------------------
// Saved teams (a whole lineup: 4 seated players + team names/colours)
// ---------------------------------------------------------------------------

/** The four players, same order every time, so the same lineup always keys the same. */
export function teamSetKey(players: SeatPlayer[]): string {
  return players
    .map((p) => nameKey(p.name))
    .sort()
    .join('|');
}

/** Saved lineups, most recently used first. */
export function getSavedTeams(): SavedTeam[] {
  return load<SavedTeam[]>(KEYS.teams, [])
    .slice()
    .sort((a, b) => b.lastUsed - a.lastUsed);
}

/** A readable default label from the two team names, e.g. "Reyzak vs Thanshu". */
export const teamSetLabel = (teams: Teams) => `${teams.A.name} vs ${teams.B.name}`;

/** Remembers a match's seating and team names as a lineup that can be reused. */
export function rememberTeamSet(seats: SeatPlayer[], teams: Teams) {
  const key = teamSetKey(seats);
  const existing = getSavedTeams().find((t) => teamSetKey(t.players) === key);
  const entry: SavedTeam = {
    label: existing?.label ?? teamSetLabel(teams),
    players: seats.map((s) => ({ playerId: s.playerId, name: s.name, avatarKey: s.avatarKey })),
    teams,
    lastUsed: Date.now(),
  };
  const list = [entry, ...getSavedTeams().filter((t) => teamSetKey(t.players) !== key)].slice(0, 20);
  save(KEYS.teams, list);
  listeners.forEach((l) => l());
}

export function renameSavedTeam(key: string, label: string) {
  save(
    KEYS.teams,
    getSavedTeams().map((t) => (teamSetKey(t.players) === key ? { ...t, label } : t)),
  );
  listeners.forEach((l) => l());
}

export function forgetSavedTeam(key: string) {
  save(
    KEYS.teams,
    getSavedTeams().filter((t) => teamSetKey(t.players) !== key),
  );
  listeners.forEach((l) => l());
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function toMatchState(data: MatchResponse): MatchState {
  return {
    rules: data.match.rules,
    firstDealer: data.match.firstDealerSeat,
    deals: data.deals,
    closed: Boolean(data.match.closedAt),
  };
}

function statusColumn(state: MatchState): MatchStatusColumn {
  const phase = matchStatus(state).phase;
  if (phase === 'awaitingFirstDealer') return 'setup';
  return phase === 'finished' ? 'finished' : 'in_progress';
}

function putMatch(raw: MatchResponse) {
  const data = upgrade(raw);
  touchRecent(data);
  // A phone that can edit a match (e.g. via an editor link) learns its players too.
  if (tokens[data.match.code] && !snap.matches[data.match.code]) rememberPlayers(data.match.seats, false);
  set({ matches: { ...snap.matches, [data.match.code]: data } });
}

function updateLocal(code: string, fn: (d: MatchResponse) => MatchResponse) {
  const current = snap.matches[code];
  if (!current) throw new Error('Match not loaded');
  const next = fn(current);
  const state = toMatchState(next);
  const status = statusColumn(state);
  putMatch({
    ...next,
    match: {
      ...next.match,
      status,
      finishedAt: status === 'finished' ? (next.match.finishedAt ?? new Date().toISOString()) : null,
      updatedAt: new Date().toISOString(),
    },
  });
}

const hasPending = (code: string) => snap.queue.some((op) => op.code === code);
export const usePendingCount = (code?: string) =>
  useStore((s) => (code ? s.queue.filter((o) => o.code === code).length : s.queue.length));

function enqueue(code: string, kind: OpKind, body: unknown) {
  const op: Op = { id: crypto.randomUUID(), code, kind, body, attempts: 0 };
  set({ queue: [...snap.queue, op] });
  void flush();
}

// ---------------------------------------------------------------------------
// Loading & creating
// ---------------------------------------------------------------------------

export async function fetchMatch(code: string): Promise<void> {
  if (snap.loading[code]) return;
  set({ loading: { ...snap.loading, [code]: true } });
  try {
    const data = await api<MatchResponse>(`/matches/${code}`);
    // Local unsynced edits win until the queue drains; then we refetch.
    if (!hasPending(code)) putMatch(data);
    const { [code]: _, ...errors } = snap.errors;
    set({ errors });
  } catch (err) {
    if (!snap.matches[code] || (err instanceof ApiError && err.status === 404)) {
      set({ errors: { ...snap.errors, [code]: err instanceof ApiError ? err.message : 'Could not load the match.' } });
    }
  } finally {
    const { [code]: _, ...loading } = snap.loading;
    set({ loading });
  }
}

export async function createMatch(body: CreateMatchBody): Promise<string> {
  const res = await api<CreateMatchResponse>('/matches', { method: 'POST', body });
  setToken(res.match.code, res.editToken);
  rememberPlayers(res.match.seats, true);
  rememberTeamSet(res.match.seats, res.match.teams);
  const { editToken: _, ...data } = res;
  putMatch(data);
  return res.match.code;
}

// ---------------------------------------------------------------------------
// Optimistic mutations
// ---------------------------------------------------------------------------

export function spin(code: string, seat: Seat) {
  updateLocal(code, (d) => ({
    ...d,
    match: { ...d.match, firstDealerSeat: seat, firstDealerSpunAt: new Date().toISOString() },
  }));
  enqueue(code, 'spin', { seat });
}

/** Throws `RuleError` if the deal is invalid; nothing is queued in that case. */
export function recordDeal(code: string, input: DealInput) {
  const current = snap.matches[code]!;
  const next = applyDeal(toMatchState(current), input);
  updateLocal(code, (d) => ({ ...d, deals: [...next.deals] }));
  enqueue(code, 'deal', { dealNo: next.deals.length, ...input });
}

export function undoDeal(code: string) {
  const current = snap.matches[code]!;
  const dealNo = current.deals.length;
  const next = undoLastDeal(toMatchState(current));
  // Undoing also reopens a finished match.
  updateLocal(code, (d) => ({ ...d, deals: [...next.deals], match: { ...d.match, closedAt: null } }));
  enqueue(code, 'undo', { dealNo });
}

/** Adds another round where every player deals `hands` more, reopening a finished match. */
/** Keep playing: one more round of `hands` each (reopens a finished match). Throws `RuleError`. */
export function continueMatch(code: string, hands: number) {
  const current = snap.matches[code]!;
  const next = continueMatchState(toMatchState(current), hands);
  updateLocal(code, (d) => ({ ...d, match: { ...d.match, rules: next.rules, closedAt: null } }));
  enqueue(code, 'continue', { hands, rounds: next.rules.extraRounds.length });
}

/** "Finish match": fewest Guaa wins. Throws `RuleError` before the first deal. */
export function finishMatch(code: string) {
  const current = snap.matches[code]!;
  finishMatchState(toMatchState(current));
  updateLocal(code, (d) => ({ ...d, match: { ...d.match, closedAt: new Date().toISOString() } }));
  enqueue(code, 'finish', {});
}

export interface MatchPatch {
  rules?: HouseRules;
  seats?: SeatPlayer[];
  teams?: Teams;
}

/** Throws `RuleError` if new rules can't be applied to the recorded deals. */
export function patchMatch(code: string, patch: MatchPatch) {
  const current = snap.matches[code]!;
  let deals = current.deals;
  if (patch.rules && current.deals.length > 0) deals = [...rescore(toMatchState(current), patch.rules).deals];
  updateLocal(code, (d) => ({ ...d, deals, match: { ...d.match, ...patch } }));
  enqueue(code, 'patch', { ...patch, confirmRulesChange: current.deals.length > 0 ? true : undefined });
}

// ---------------------------------------------------------------------------
// Queue processing
// ---------------------------------------------------------------------------

let retryTimer: ReturnType<typeof setTimeout> | undefined;

function send(op: Op): Promise<MatchResponse> {
  const token = getToken(op.code);
  const base = `/matches/${op.code}`;
  switch (op.kind) {
    case 'spin':
      return api(`${base}/spin`, { method: 'POST', body: op.body, token });
    case 'deal':
      return api(`${base}/deals`, { method: 'POST', body: op.body, token });
    case 'undo':
      return api(`${base}/deals/last?dealNo=${(op.body as { dealNo: number }).dealNo}`, { method: 'DELETE', token });
    case 'patch':
      return api(base, { method: 'PATCH', body: op.body, token });
    case 'finish':
      return api(`${base}/finish`, { method: 'POST', body: {}, token });
    case 'continue':
      return api(`${base}/continue`, { method: 'POST', body: op.body, token });
  }
}

export async function flush(): Promise<void> {
  if (snap.syncing || !snap.online) return;
  clearTimeout(retryTimer);
  set({ syncing: true });
  try {
    while (snap.queue.length > 0) {
      const op = snap.queue[0]!;
      try {
        const data = await send(op);
        set({ queue: snap.queue.slice(1) });
        if (!hasPending(op.code)) putMatch(data);
      } catch (err) {
        if (err instanceof ApiError && err.permanent) {
          // The server rejected it: local state is wrong. Drop this match's ops and resync.
          set({ queue: snap.queue.filter((o) => o.code !== op.code) });
          toast(`Couldn't save: ${err.message}. Reloaded the latest version.`, 'error', 6000);
          void fetchMatch(op.code);
          continue;
        }
        const attempts = op.attempts + 1;
        set({ queue: [{ ...op, attempts }, ...snap.queue.slice(1)] });
        if (err instanceof NetworkError) set({ online: navigator.onLine });
        const delay = Math.min(30_000, 1000 * 2 ** Math.min(attempts, 5));
        retryTimer = setTimeout(() => void flush(), delay);
        return;
      }
    }
  } finally {
    set({ syncing: false });
  }
}

window.addEventListener('online', () => {
  set({ online: true });
  void flush();
});
window.addEventListener('offline', () => set({ online: false }));
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') void flush();
});
void flush();
